import { useContext, useRef, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useDialogFocus } from "../../hooks/useDialogFocus";
import { AnimatePhaseContext } from "./AnimatePhaseContext";

interface DialogProps {
  /** Called on Escape, on a backdrop click (unless disabled), and expected
   * to also drive any visible close button the dialog renders. */
  onClose: () => void;
  /** "alertdialog" for a confirmation; "dialog" (default) otherwise. */
  role?: "dialog" | "alertdialog";
  /** Accessible name, for dialogs with no visible heading to point at. */
  label?: string;
  /** Id of the element (usually the visible heading) that names the dialog.
   * Takes precedence over `label` when both are given. */
  labelledBy?: string;
  /** Extra classes for the backdrop — typically just the stacking z-index. */
  backdropClassName?: string;
  /** Extra classes for the panel — width, height, layout, overflow. */
  panelClassName?: string;
  /** Close when the backdrop itself (not the panel) is clicked. Default true. */
  closeOnBackdropClick?: boolean;
  /** Pass false to ignore Escape (e.g. while a save is in flight). Default true. */
  escapeEnabled?: boolean;
  children: ReactNode;
}

/**
 * Shared modal-dialog shell: backdrop, role/aria-modal/label, and keyboard
 * accessibility (initial focus, Tab trap, restore — see useDialogFocus).
 *
 * Portaled to `document.body` so a parent `transform` (the enter animation's
 * fill-mode) cannot become the containing block for this `fixed` overlay.
 * Enter/exit classes still follow AnimatePresence via AnimatePhaseContext.
 *
 * Escape uses the existing LIFO stack so stacked dialogs close top-down.
 */
export function Dialog({
  onClose,
  role = "dialog",
  label,
  labelledBy,
  backdropClassName = "",
  panelClassName = "",
  closeOnBackdropClick = true,
  escapeEnabled = true,
  children,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const phase = useContext(AnimatePhaseContext);
  useEscapeKey(onClose, { enabled: escapeEnabled });
  useDialogFocus(panelRef);

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div data-animate-phase={phase} style={{ display: "contents" }}>
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 animate-dialog-backdrop ${backdropClassName}`}
        onClick={handleBackdropClick}
      >
        <div
          ref={panelRef}
          role={role}
          aria-modal="true"
          aria-label={labelledBy ? undefined : label}
          aria-labelledby={labelledBy}
          tabIndex={-1}
          className={`bg-warm-bg-card rounded-xl border border-warm-border p-4 sm:p-6 animate-dialog-panel ${panelClassName}`}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default Dialog;
